import { HelptextProvider, LabelProvider, RequiredProvider } from './../../../../structure/component/plam/his-plam.component';
import { Component, output, model, inject, signal, effect, computed, input, linkedSignal, forwardRef, ChangeDetectionStrategy } from '@angular/core';
import { HisControlValueAccessor } from '../../../../core/forms/his-control-value-accessor';
import { AgreementDefinitionDto } from '../model/agreement-definition.dto';
import { InputPersonalAgreementChangeEvent } from '../model/input-personal-agreement-change.event';
import { PersonalAgreement } from '../model/personal-agreement.model';
import { AgreementStatus } from '../model/agreement-status.enum';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { PersonalAgreementService } from '../service/his-input-personal-agreement.service';
import { Subscription } from 'rxjs';
import { AgreementType } from '../model/agreement-type.enum';
import { HisUiService } from '../../../../core/service/base/his-ui.service';
import { InputPersonalAgreementUiText } from '../model/ui-texts';

@Component( {
    standalone: false,
    selector: 'his-input-personal-agreement',
    templateUrl: './his-input-personal-agreement.component.html',
    styleUrl: './his-input-personal-agreement.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef( () => InputPersonalAgreementComponent ),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef( () => InputPersonalAgreementComponent ),
            multi: true,
        },
    ],
} )
export class InputPersonalAgreementComponent extends HisControlValueAccessor implements Validator, LabelProvider, HelptextProvider, RequiredProvider {
    //Constants
    protected readonly agreementStatus = AgreementStatus;
    protected readonly uiText = InputPersonalAgreementUiText;

    // Inputs
    readonly value = model<PersonalAgreement>();
    readonly mandatory = input<boolean | undefined>( undefined );
    readonly disabled = input<boolean | undefined>( undefined );
    readonly requiredValidationMessageKeyTypeExplicit = input<string>( InputPersonalAgreementUiText.TYPE_EXPLICIT_REQUIRED_VALIDATION );
    readonly requiredValidationMessageKeyTypeImplicit = input<string>( InputPersonalAgreementUiText.TYPE_IMPLICIT_REQUIRED_VALIDATION );
    readonly disableConsent = input<boolean | undefined>( undefined );
    readonly disableDecline = input<boolean | undefined>( undefined );
    readonly readonlyWhenModerationPending = input<boolean | undefined>( undefined );
    readonly agreementType = input<AgreementType | undefined>( undefined );
    readonly hideAgreementText = input<boolean | undefined>( undefined );

    // Outputs
    readonly valueChanged = output<InputPersonalAgreementChangeEvent>();

    protected readonly agreementDefinition = signal<AgreementDefinitionDto | undefined>( undefined );

    //this indirection is used to protect the effect (see constructor) from being triggered by changes to other properties of the value than personalAgreementDefinitionId
    private readonly agreementDefinitionId = computed( () => this.value()?.personalAgreementDefinitionId );

    private agreementDefinitionSubscription?: Subscription;

    protected readonly disabledIntern = linkedSignal( () => this.disabled() );

    private readonly effectiveAgreementType = computed( () =>
        this.agreementType() ?? this.agreementDefinition()?.agreementType
    );

    protected readonly isTypeExplicit = computed( () => this.effectiveAgreementType() === AgreementType.EXPLICIT );

    protected readonly implicitAgreementLabel = computed( () => this.getImplicitAgreementLabel() );

    protected readonly explicitAgreementConsentLabel = computed( () => this.agreementDefinition()?.acceptText || this.hisUiService.translate( InputPersonalAgreementUiText.EXPLICIT_AGREEMENT_CONSENT_LABEL ) );
    protected readonly explicitAgreementDeclineLabel = computed( () => this.agreementDefinition()?.declineText || this.hisUiService.translate( InputPersonalAgreementUiText.EXPLICIT_AGREEMENT_DECLINE_LABEL ) );

    private readonly requiredValidationMessageKey = computed( () => this.getRequiredValidationMessageKey() );
    protected readonly requiredValidationMessage = computed( () => this.getRequiredValidationMessage() );

    private readonly isInvalid = computed( () => this._isInvalid( this.value() ) );

    private readonly isReadonlyDueToPendingModeration = computed( () => !!( this.readonlyWhenModerationPending() && this.value()?.pendingModeration ) );

    protected readonly agreementTextVisible = linkedSignal( () => !this.hideAgreementText() );
    protected readonly renderAgreementText = computed( () => this.agreementTextVisible() && this.agreementDefinition()?.agreementText );

    //Implementation of ControlValueAccessor method
    public override writeValue = this.value.set;
    private onValidatorChange?: () => void;

    //used by his-plam
    ddLabeltext: string;
    required: boolean;
    ddHelptext: string;

    //Dependencies
    private readonly personalAgreementService = inject( PersonalAgreementService );
    private readonly hisUiService = inject( HisUiService );

    protected readonly isConsentDisabled = computed( () => this.calculateConsentDisabled() );

    protected readonly isDeclineDisabled = computed( () => this.calculateDeclineDisabled() );

    protected readonly toggleAgreementTextButtonLabel = computed( () => this.calculateToggleButtonLabel() );

    constructor() {
        super();

        //keep agreementDefinition up to date with the current value's personalAgreementDefinitionId
        effect( () => {
            const id = this.agreementDefinitionId();
            if ( !id || id < 0 ) {
                this.agreementDefinition.set( undefined );
                return;
            }
            this.loadAgreementDefinition( id );
        } );

        //keep required up to date with the current value's personalAgreementDefinitionId and mandatory property
        //input value should have priority over agreement definition
        effect( () => {
            const externalMandatory = this.mandatory();
            const internalMandatory = this.agreementDefinition()?.mandatory;

            this.required = externalMandatory ?? internalMandatory ?? false;
        } );
    }

    validate( control: AbstractControl ): ValidationErrors | null {
        if ( this._isInvalid( control.value ) ) {
            return {
                required: {
                    message: this.requiredValidationMessage()
                }
            };
        }
        return null;
    }

    registerOnValidatorChange( fn: () => void ): void {
        this.onValidatorChange = fn;
    }

    setDisabledState( isDisabled: boolean ): void {
        this.disabledIntern.set( isDisabled );
    }

    protected onStatusChange( isChecked: boolean ): void {
        const current = this.value();

        const previous = { ...current };
        const updated = {
            ...current,
            status: isChecked ? AgreementStatus.CONSENTED : AgreementStatus.DECLINED,
            changeDate: new Date()
        };

        this.value.set( updated );
        this.onModelChange( updated );
        this.onModelTouched();

        this.valueChanged.emit( { oldValue: previous, newValue: updated } );
    }

    protected toggleAgreementText(): void {
        this.agreementTextVisible.set( !this.agreementTextVisible() );
    }

    private loadAgreementDefinition( agreementDefinitionId: number ): void {
        this.agreementDefinitionSubscription?.unsubscribe();

        this.agreementDefinitionSubscription = this.personalAgreementService
            .findAgreementDefinitionById( agreementDefinitionId )
            .subscribe( ( def: AgreementDefinitionDto ) => {
                this.agreementDefinition.set( def );
                this.ddLabeltext = def?.defaulttext;
                this.ddHelptext = def?.helptext;

                this.onValidatorChange?.();
            } );
    }

    private getImplicitAgreementLabel(): string {
        const definition = this.agreementDefinition();
        const agreementDisplayName = definition?.defaulttext ?? this.hisUiService.translate( InputPersonalAgreementUiText.AGREEMENT_TEXT );

        return this.hisUiService.translate( InputPersonalAgreementUiText.IMPLICIT_AGREEMENT_LABEL, agreementDisplayName );
    }

    private getRequiredValidationMessageKey(): string | null {
        const isRequired = this.mandatory() ?? this.agreementDefinition()?.mandatory ?? false;

        if ( !isRequired ) {
            return null;
        }

        return this.isTypeExplicit()
            ? this.requiredValidationMessageKeyTypeExplicit()
            : this.requiredValidationMessageKeyTypeImplicit();
    }

    private getRequiredValidationMessage(): string | null {
        if ( !this.isInvalid() ) {
            return null;
        }

        const validationMessageKey = this.requiredValidationMessageKey();
        if ( !validationMessageKey ) {
            return null;
        }

        const agreementDisplayName =
            this.agreementDefinition()?.defaulttext
            ?? this.hisUiService.translate( InputPersonalAgreementUiText.AGREEMENT_TEXT );

        return this.hisUiService.translate( validationMessageKey, agreementDisplayName );
    }

    private _isInvalid( value: PersonalAgreement | null | undefined ): boolean {
        const isRequired = this.mandatory() ?? this.agreementDefinition()?.mandatory ?? false;
        if ( !isRequired ) {
            return false;
        }

        const status = value?.status;

        if ( this.isTypeExplicit() ) {
            return status === undefined || status === AgreementStatus.UNKNOWN;
        }

        return status !== AgreementStatus.CONSENTED;
    }

    private calculateConsentDisabled(): boolean {
        return (
            this.disabledIntern() ||
            this.disableConsent() ||
            this.isReadonlyDueToPendingModeration()
        );
    }

    private calculateDeclineDisabled(): boolean {
        return (
            this.disabledIntern() ||
            this.disableDecline() ||
            this.isReadonlyDueToPendingModeration()
        );
    }

    private calculateToggleButtonLabel(): string {
        const definition = this.agreementDefinition();
        const customShowLinkText = definition?.showAgreementLinkText;
        const customHideLinkText = definition?.hideAgreementLinkText;
        const defaultName = this.hisUiService.translate( 'common.components.inputPersonalAgreement.agreementText' );

        if ( this.agreementTextVisible() ) {
            return customHideLinkText || this.hisUiService.translate( 'common.hideOf', defaultName );
        }
        return customShowLinkText || this.hisUiService.translate( 'common.showOf', defaultName );
    }
}
